import type { PathArray, PathSegment } from "../types.ts";
import type { SegmentProperties } from "../interface.ts";
import { parsePathString } from "../parser/parsePathString.ts";
import { getTotalLength } from "./getTotalLength.ts";

/**
 * Returns the segment, its index and length as well as
 * the length to that segment at a given length in a path.
 *
 * @param pathInput target `pathArray`
 * @param distance the given length
 * @returns the requested properties
 */
export const getPropertiesAtLength = <T extends PathArray>(
  pathInput: string | T,
  distance?: number,
): SegmentProperties => {
  const pathArray = parsePathString(pathInput);

  let pathTemp = pathArray.slice(0) as T;
  let pathLength = getTotalLength(pathTemp);
  let index = pathTemp.length - 1;
  let lengthAtSegment = 0;
  let length = 0;
  let segment = pathArray[0] as PathSegment;

  // If the path is empty, return 0.
  if (index <= 0 || !distance || !Number.isFinite(distance)) {
    return {
      segment,
      index: 0,
      length,
      lengthAtSegment,
    };
  }

  if (distance >= pathLength) {
    pathTemp = pathArray.slice(0, -1) as T;
    lengthAtSegment = getTotalLength(pathTemp);
    length = pathLength - lengthAtSegment;
    segment = pathArray[index];
    return {
      segment,
      index,
      length,
      lengthAtSegment,
    };
  }

  const segments = [] as SegmentProperties[];
  while (index > 0) {
    segment = pathTemp[index];
    pathTemp = pathTemp.slice(0, -1) as T;
    lengthAtSegment = getTotalLength(pathTemp);
    length = pathLength - lengthAtSegment;
    pathLength = lengthAtSegment;

    segments.push({
      segment,
      index,
      length,
      lengthAtSegment,
    });
    index -= 1;
  }

  return segments.find(
    ({ lengthAtSegment: l }) => l <= distance,
  ) as SegmentProperties;
};
